import { THIRTY_MINUTES_IN_SECONDS } from '@core/constants/time'
import type { Corridor } from '@core/types/imt'

type ComparisonProperties = {
  countryFrom: string
  countryTo: string
  currencyFrom: string
  currencyTo: string
  amount?: number
  payout?: string
}

function compareCorridorAndComparison(
  corridor: ComparisonProperties,
  comparison: ComparisonProperties,
): boolean {
  return (
    comparison.countryFrom === corridor.countryFrom &&
    comparison.countryTo === corridor.countryTo &&
    comparison.currencyFrom === corridor.currencyFrom &&
    comparison.currencyTo === corridor.currencyTo &&
    comparison.amount === corridor.amount &&
    comparison.payout === corridor.payout
  )
}

const LEGACY_LAST_COMPARISON_INFO = 'lastImtComparisonInfo'
const LAST_IMT_COMPARISONS = 'lastImtComparisons'

export type ComparisonInfo = {
  countryFrom: string
  countryTo: string
  currencyFrom: string
  currencyTo: string
  amount: number
  payout: string | undefined
  cheapestProvider: {
    name: string
    slug: string
  } | null
  spotlightedProvider: {
    name: string
    slug: string
  } | null
  liveComparison: boolean
  midMarketRate: number
  timestamp: number
  results?: unknown
}

function updateLastComparisons(
  info: ComparisonInfo,
  existingSearches: ComparisonInfo[] | null,
): ComparisonInfo[] | null {
  if (!existingSearches) {
    return [info]
  }
  const foundIndex = existingSearches.findIndex((el) => compareCorridorAndComparison(el, info))
  if (foundIndex !== -1) {
    const copy = existingSearches.filter((_, index) => index !== foundIndex)
    return [info, ...copy]
  }
  return [info, ...existingSearches].slice(0, 5)
}

export async function storeLastComparisonInfo(info: ComparisonInfo) {
  const existingSearches = await storage.getValue<ComparisonInfo[]>(LAST_IMT_COMPARISONS)
  const updatedList = updateLastComparisons(info, existingSearches)
  await storage.setValue(LAST_IMT_COMPARISONS, updatedList)
}

export async function getLastComparisons(): Promise<ComparisonInfo[]> {
  const lastComparisonInfo: ComparisonInfo | null = await storage.getValue(
    LEGACY_LAST_COMPARISON_INFO,
  )
  if (lastComparisonInfo && Object.keys(lastComparisonInfo).length) {
    await storage.setValue(LAST_IMT_COMPARISONS, [lastComparisonInfo])
    await storage.removeValue(LEGACY_LAST_COMPARISON_INFO)
    return [lastComparisonInfo]
  }
  return (await storage.getValue(LAST_IMT_COMPARISONS)) || []
}

export async function getLastComparisonInfo(): Promise<ComparisonInfo | null> {
  const allComparisons = await getLastComparisons()
  if (allComparisons.length > 0) {
    return allComparisons[0]
  }
  return null
}

export async function getLastComparisonResults<T>(corridor?: Corridor) {
  const allComparisons = await getLastComparisons()
  const foundComparison = findComparisonByCorridor(corridor, allComparisons)
  if (!foundComparison) {
    return null
  }

  const thirtyMinutesAgo = Date.now() - THIRTY_MINUTES_IN_SECONDS * 1000

  if (foundComparison.timestamp && foundComparison.timestamp > thirtyMinutesAgo) {
    return foundComparison.results as T
  }
  return null
}

function findComparisonByCorridor(
  corridor: Corridor | undefined,
  allComparisons: ComparisonInfo[],
) {
  if (!corridor) {
    return null
  }

  return allComparisons.find((comparison) =>
    compareCorridorAndComparison(
      {
        ...corridor,
        countryFrom: corridor.countryFrom.iso,
        countryTo: corridor.countryTo.iso,
        currencyFrom: corridor.currencyFrom.iso,
        currencyTo: corridor.currencyTo.iso,
      },
      comparison,
    ),
  )
}

export async function clearLastComparisons() {
  await storage.removeValue(LAST_IMT_COMPARISONS)
}
